import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { useProfile } from '../context/ProfileContext';
import { fetchExerciseNote, saveExerciseNote } from '../services/exerciseNotesService';
import './comic.css';

/**
 * Shared popup for viewing/editing one exercise's note for one calendar day
 * (see exerciseNotesService.js) -- opened from the live Mission (writing a
 * note during a workout), "Previously..." history entries, and the "i"
 * exercise-history popup's per-day groups (both reviewing/editing a note
 * written earlier). Same view/edit surface everywhere: it always loads the
 * current text (if any) into an editable textarea, so there's no separate
 * read-only mode to keep in sync with the editable one.
 */
const ExerciseNoteModal = ({ exerciseId, exerciseName, dayKey, dayLabel, userId, tabKey, onClose, onSaved }) => {
  const { activeProfile } = useProfile();
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !activeProfile || !exerciseId || !dayKey) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchExerciseNote(userId, activeProfile.id, exerciseId, dayKey).then((note) => {
      if (cancelled) return;
      setText(note);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, activeProfile, exerciseId, dayKey]);

  const handleSave = async () => {
    if (!activeProfile || saving) return;
    setSaving(true);
    setError(null);
    const result = await saveExerciseNote(userId, activeProfile.id, exerciseId, dayKey, text);
    setSaving(false);
    if (!result.success) {
      setError("Couldn't save that note. Try again.");
      return;
    }
    if (onSaved) onSaved(text.trim());
    onClose();
  };

  return (
    <Modal title={`${exerciseName} Note${dayLabel ? ` — ${dayLabel}` : ""}`} tabKey={tabKey} onClose={onClose}>
      {loading ? (
        <p className="comic-empty">Loading...</p>
      ) : (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="How did it feel? Form cues, aches, anything worth remembering next time..."
            className="comic-input"
            style={{ width: "100%", minHeight: "120px", resize: "vertical", fontFamily: "'Anonymous Pro', monospace" }}
            autoFocus
          />
          <button type="button" onClick={handleSave} disabled={saving} className="comic-btn comic-btn--go">
            {saving ? "Saving..." : "Save Note"}
          </button>
          <button type="button" onClick={onClose} disabled={saving} className="comic-btn comic-btn--end">
            Cancel
          </button>
          {error && <div className="comic-alert-bubble">{error}</div>}
        </>
      )}
    </Modal>
  );
};

export default ExerciseNoteModal;
